// src/pages/patient/MyConsultations.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Clock, Video, MapPin, User, Star,
  CheckCircle, XCircle, AlertCircle, Eye, Download
} from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/ui/Button';
import Alert from '../../components/ui/Alert';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {Tabs} from '../../components/ui/Tabs';
import Badge from '../../components/ui/Badge';

const MyConsultations = () => {
 
  
  // State management
  const [loading, setLoading] = useState(true);
  const [consultations, setConsultations] = useState([]);
  const [groupedConsultations, setGroupedConsultations] = useState({
    upcoming: [],
    completed: [],
    cancelled: []
  });
  const [stats, setStats] = useState({});
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Rating form
  const [rating, setRating] = useState({
    score: 5,
    comment: ''
  });

  // Cancel form
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    loadConsultations();
  }, []);

  const loadConsultations = async () => {
  try {
    setLoading(true);
    setError('');

    const response = await apiService.consultations.getHistory();
    
    // Add defensive checks
    if (!response || !response.data) {
      throw new Error('Invalid response from server');
    }

    const data = response.data;

    // Set consultations with fallback to empty array
    setConsultations(data.consultations || []);
    setGroupedConsultations(data.grouped || {
      upcoming: [],
      completed: [],
      cancelled: []
    });
    setStats(data.stats || {
      total: 0,
      upcoming: 0,
      completed: 0,
      cancelled: 0
    });
  } catch (error) {
    console.error('Load consultations error:', error);
    setError(error.message || 'Failed to load consultations. Please try again.');
    
    // Set empty state on error
    setConsultations([]);
    setGroupedConsultations({
      upcoming: [],
      completed: [],
      cancelled: []
    });
    setStats({
      total: 0,
      upcoming: 0,
      completed: 0,
      cancelled: 0
    });
  } finally {
    setLoading(false);
  }
};

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: AlertCircle,
      accepted: CheckCircle,
      rejected: XCircle,
      completed: CheckCircle,
      cancelled: XCircle
    };
    return icons[status] || AlertCircle;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openDetailsModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedConsultation(null);
  };

  const openRatingModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowRatingModal(true);
    setRating({ score: 5, comment: '' });
  };

  const closeRatingModal = () => {
    setShowRatingModal(false);
    setSelectedConsultation(null);
    setRating({ score: 5, comment: '' });
  };

  const openCancelModal = (consultation) => {
    setSelectedConsultation(consultation);
    setShowCancelModal(true);
    setCancelReason('');
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setSelectedConsultation(null);
    setCancelReason('');
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    
    if (!selectedConsultation) return;

    try {
      setLoading(true);
      setError('');

      await apiService.consultations.rate(selectedConsultation._id, rating);

      setSuccess('Thank you for your feedback!');
      closeRatingModal();
      loadConsultations(); // Reload to show updated rating
    } catch (error) {
      console.error('Submit rating error:', error);
      setError(error.response?.data?.message || 'Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConsultation = async (e) => {
    e.preventDefault();
    
    if (!selectedConsultation) return;

    try {
      setLoading(true);
      setError('');

      await apiService.consultations.cancel(selectedConsultation._id, {
        reason: cancelReason
      });

      setSuccess('Consultation cancelled successfully.');
      closeCancelModal();
      loadConsultations(); // Reload to show updated status
    } catch (error) {
      console.error('Cancel consultation error:', error);
      setError(error.response?.data?.message || 'Failed to cancel consultation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ConsultationCard = ({ consultation }) => {
    const StatusIcon = getStatusIcon(consultation.status);
    const canRate = consultation.status === 'completed' && (!consultation.rating || !consultation.rating.score);
    const canCancel = ['pending', 'accepted'].includes(consultation.status);

    return (
      <div className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Dr. {consultation.doctor?.firstName} {consultation.doctor?.lastName}
              </h3>
              <p className="text-sm text-gray-600">{consultation.doctor?.specialization}</p>
            </div>
          </div>
          <Badge className={getStatusColor(consultation.status)}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {consultation.status}
          </Badge>
        </div>

        {/* Consultation Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            {consultation.type === 'online' ? (
              <Video className="w-4 h-4 mr-2 text-blue-600" />
            ) : (
              <MapPin className="w-4 h-4 mr-2 text-purple-600" />
            )}
            <span className="capitalize">{consultation.type} Consultation</span>
          </div>
          
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="w-4 h-4 mr-2" />
            <span>
              {consultation.scheduledAt 
                ? formatDate(consultation.scheduledAt)
                : `Requested: ${formatDate(consultation.createdAt)}`
              }
            </span>
          </div>

          {consultation.fee && (
            <div className="flex items-center text-sm text-gray-600">
              <span className="font-medium">Fee: KES {consultation.fee.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Symptoms Preview */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <p className="text-xs font-medium text-gray-700 mb-1">Symptoms:</p>
          <p className="text-sm text-gray-600 line-clamp-2">
            {consultation.symptoms}
          </p>
        </div>

        {/* Rating Display */}
        {consultation.rating && consultation.rating.score && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-center">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < consultation.rating.score
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-900">
                Your Rating
              </span>
            </div>
            {consultation.rating.comment && (
              <p className="mt-2 text-sm text-gray-600">
                "{consultation.rating.comment}"
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => openDetailsModal(consultation)}
          >
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </Button>
          
          <div className="flex space-x-2">
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openCancelModal(consultation)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
            {canRate && (
              <Button
                size="sm"
                onClick={() => openRatingModal(consultation)}
              >
                <Star className="w-4 h-4 mr-2" />
                Rate
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    {
      key: 'upcoming',
      label: 'Upcoming',
      count: groupedConsultations.upcoming?.length || 0
    },
    {
      key: 'completed',
      label: 'Completed',
      count: groupedConsultations.completed?.length || 0
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      count: groupedConsultations.cancelled?.length || 0
    }
  ];

  if (loading && consultations.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Consultations</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your consultation history
          </p>
        </div>
        <Link to="/patient/book-consultation">
          <Button>
            <Calendar className="w-4 h-4 mr-2" />
            Book New Consultation
          </Button>
        </Link>
      </div>

      {/* Success Alert */}
      {success && (
        <Alert type="success" title="Success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert type="error" title="Error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
            </div>
            <Calendar className="w-8 h-8 text-primary-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-blue-600">{stats.upcoming || 0}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cancelled</p>
              <p className="text-2xl font-bold text-gray-600">{stats.cancelled || 0}</p>
            </div>
            <XCircle className="w-8 h-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {/* Content */}
        <div className="p-6">
          {groupedConsultations[activeTab]?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {groupedConsultations[activeTab].map(consultation => (
                <ConsultationCard key={consultation._id} consultation={consultation} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab} consultations
              </h3>
              <p className="text-gray-500 mb-4">
                {activeTab === 'upcoming' 
                  ? "You don't have any upcoming consultations"
                  : `You don't have any ${activeTab} consultations`
                }
              </p>
              {activeTab === 'upcoming' && (
                <Link to="/patient/book-consultation">
                  <Button>Book Your First Consultation</Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedConsultation && (
        <Modal
          isOpen={showDetailsModal}
          onClose={closeDetailsModal}
          title="Consultation Details"
          size="lg"
        >
          <div className="space-y-6">
            {/* Doctor Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Dr. {selectedConsultation.doctor?.firstName} {selectedConsultation.doctor?.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{selectedConsultation.doctor?.specialization}</p>
                </div>
              </div>
              {selectedConsultation.doctor?.hospital && (
                <p className="text-sm text-gray-600">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {selectedConsultation.doctor.hospital.name}
                </p>
              )}
            </div>

            {/* Consultation Info */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Consultation Information</h4>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Type:</dt>
                  <dd className="text-sm font-medium text-gray-900 capitalize">
                    {selectedConsultation.type}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Status:</dt>
                  <dd>
                    <Badge className={getStatusColor(selectedConsultation.status)}>
                      {selectedConsultation.status}
                    </Badge>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Scheduled:</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {formatDate(selectedConsultation.scheduledAt)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Fee:</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    KES {selectedConsultation.fee?.toLocaleString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Symptoms */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Symptoms</h4>
              <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                {selectedConsultation.symptoms}
              </p>
            </div>

            {/* Patient Notes */}
            {selectedConsultation.patientNotes && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Your Notes</h4>
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  {selectedConsultation.patientNotes}
                </p>
              </div>
            )}

            {/* Diagnosis & Prescription (if completed) */}
            {selectedConsultation.status === 'completed' && (
              <>
                {selectedConsultation.diagnosis && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Diagnosis</h4>
                    <p className="text-sm text-gray-600 bg-green-50 border border-green-200 rounded-lg p-3">
                      {selectedConsultation.diagnosis}
                    </p>
                  </div>
                )}

                {selectedConsultation.prescription && selectedConsultation.prescription.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Prescription</h4>
                    <ul className="space-y-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      {selectedConsultation.prescription.map((med, index) => (
                        <li key={index} className="text-sm text-gray-700">
                          • {med}
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Prescription
                    </Button>
                  </div>
                )}

                {selectedConsultation.recommendations && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                      {selectedConsultation.recommendations}
                    </p>
                  </div>
                )}

                {selectedConsultation.followUpDate && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-yellow-900">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Follow-up: {formatDate(selectedConsultation.followUpDate)}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Doctor Notes */}
            {selectedConsultation.doctorNotes && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Doctor's Notes</h4>
                <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                  {selectedConsultation.doctorNotes}
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Rating Modal */}
      {showRatingModal && selectedConsultation && (
        <Modal
          isOpen={showRatingModal}
          onClose={closeRatingModal}
          title="Rate Your Consultation"
        >
          <form onSubmit={handleSubmitRating} className="space-y-6">
            {/* Doctor Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-medium text-gray-900">
                Dr. {selectedConsultation.doctor?.firstName} {selectedConsultation.doctor?.lastName}
              </p>
              <p className="text-sm text-gray-600">{selectedConsultation.doctor?.specialization}</p>
            </div>

            {/* Rating Stars */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Your Rating *
              </label>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRating(prev => ({ ...prev, score: value }))}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${
                        value <= rating.score
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                <span className="ml-3 text-lg font-medium text-gray-900">
                  {rating.score} {rating.score === 1 ? 'Star' : 'Stars'}
                </span>
              </div>
            </div>

            {/* Review Comment */}
            <div>
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
                Your Review (Optional)
              </label>
              <textarea
                id="comment"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Share your experience with the doctor..."
                value={rating.comment}
                onChange={(e) => setRating(prev => ({ ...prev, comment: e.target.value }))}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={closeRatingModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                loading={loading}
              >
                Submit Rating
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedConsultation && (
        <Modal
          isOpen={showCancelModal}
          onClose={closeCancelModal}
          title="Cancel Consultation"
        >
          <form onSubmit={handleCancelConsultation} className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-red-900">Confirm Cancellation</h4>
                  <p className="mt-1 text-sm text-red-700">
                    Are you sure you want to cancel this consultation with Dr. {selectedConsultation.doctor?.firstName} {selectedConsultation.doctor?.lastName}?
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Cancellation (Optional)
              </label>
              <textarea
                id="cancelReason"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Please let us know why you're cancelling..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={closeCancelModal}
                disabled={loading}
              >
                Keep Consultation
              </Button>
              <Button
                type="submit"
                variant="danger"
                disabled={loading}
                loading={loading}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancel Consultation
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default MyConsultations;